import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Loader2, ShieldAlert, Lock, Puzzle, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/**
 * Settings → Modules → Permissions (plan Task 2.6).
 *
 * A tenant admin allocates scopes to an installed custom module, WITHIN the
 * ceiling the control plane approved at install. Three properties this UI has to
 * get across, because getting them wrong is a security-relevant misunderstanding:
 *
 *  1. The ceiling is a HARD boundary, not a default. Scopes outside it are shown
 *     but permanently disabled — visible so an admin understands what was withheld
 *     and can ask for it, un-grantable so they cannot try and get a 422.
 *  2. Elevated scopes are visually distinct and show the manifest justification
 *     inline. They skip the acting-user permission layer, so an admin granting one
 *     is making a materially different decision from granting a normal scope, and
 *     must not have to infer that from a `.all` suffix.
 *  3. Nothing is granted by default. A freshly installed module holds nothing until
 *     an admin deliberately grants it.
 */
export default function ModulePermissionsTab() {
    const { getAuthHeader } = useAuth();
    const [modules, setModules] = useState([]);
    const [scopeCatalog, setScopeCatalog] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingSlug, setSavingSlug] = useState(null);
    // Pending edits per module, so an admin can tick several boxes and save once.
    const [draft, setDraft] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const headers = getAuthHeader();
            const [modulesRes, scopesRes] = await Promise.all([
                axios.get(`${API}/v1/custom-modules`, { headers }),
                axios.get(`${API}/v1/custom-modules/scopes`, { headers }),
            ]);
            const installed = modulesRes.data?.modules || [];
            setModules(installed);
            // Keyed by name: every render needs a scope's metadata (elevated,
            // description) by name, and the backend is the only source of truth for
            // it — duplicating the registry in the frontend would let the UI drift
            // from what the resolver actually enforces.
            const catalog = {};
            (scopesRes.data?.scopes || []).forEach(scope => { catalog[scope.name] = scope; });
            setScopeCatalog(catalog);
            const initialDraft = {};
            installed.forEach(mod => { initialDraft[mod.slug] = [...(mod.allocated || [])]; });
            setDraft(initialDraft);
        } catch (err) {
            toast.error(err.response?.data?.detail?.message || 'Failed to load module permissions');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const toggleScope = (slug, scopeName) => {
        setDraft(prev => {
            const current = prev[slug] || [];
            return {
                ...prev,
                [slug]: current.includes(scopeName)
                    ? current.filter(name => name !== scopeName)
                    : [...current, scopeName],
            };
        });
    };

    const save = async (slug) => {
        setSavingSlug(slug);
        try {
            const res = await axios.put(
                `${API}/v1/custom-modules/${slug}/permissions`,
                { allocated: draft[slug] || [] },
                { headers: getAuthHeader() },
            );
            setModules(prev => prev.map(mod => (
                mod.slug === slug ? { ...mod, allocated: res.data.allocated } : mod
            )));
            toast.success('Module permissions updated');
        } catch (err) {
            const detail = err.response?.data?.detail;
            // SCOPE_EXCEEDS_CEILING deserves its own message: it is not a generic
            // validation failure, it means the platform withheld that scope and only
            // a control-plane approval change can grant it.
            if (detail?.detail?.code === 'SCOPE_EXCEEDS_CEILING') {
                toast.error(
                    `Outside the approved ceiling: ${(detail.detail.exceeded || []).join(', ')}. `
                    + 'Widening requires a control-plane approval change.',
                );
            } else {
                toast.error(detail?.message || 'Failed to update module permissions');
            }
        } finally {
            setSavingSlug(null);
        }
    };

    const isDirty = (mod) => {
        const pending = [...(draft[mod.slug] || [])].sort();
        const saved = [...(mod.allocated || [])].sort();
        return JSON.stringify(pending) !== JSON.stringify(saved);
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-stone-500 py-8">
                <Loader2 className="w-4 h-4 animate-spin" />Loading module permissions…
            </div>
        );
    }

    if (modules.length === 0) {
        return (
            <div className="max-w-2xl">
                <div className="flex items-start gap-3 p-4 rounded-lg border border-stone-200 bg-stone-50">
                    <Puzzle className="w-5 h-5 text-stone-400 mt-0.5 shrink-0" />
                    <div className="text-sm text-stone-600">
                        <p className="font-medium text-stone-700">No custom modules installed</p>
                        <p className="mt-1">
                            Custom modules are installed by a platform administrator. Once one is
                            installed here, you control exactly which data it may reach from this
                            screen.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl space-y-8">
            <div className="flex items-start gap-3 p-4 rounded-lg border border-stone-200 bg-stone-50">
                <Info className="w-5 h-5 text-stone-400 mt-0.5 shrink-0" />
                <div className="text-sm text-stone-600">
                    A module can only reach the data you grant it here, and only ever on behalf
                    of a user who already has permission for it themselves. Scopes the platform
                    did not approve for this installation cannot be granted from this screen.
                </div>
            </div>

            {modules.map(mod => {
                const ceiling = mod.scope_ceiling || [];
                const pending = draft[mod.slug] || [];
                // Show every registry scope so an admin can see what was withheld,
                // not just what is grantable — "why can't this module do X" is the
                // question this screen should answer without a support ticket.
                const allScopeNames = Object.keys(scopeCatalog).sort();

                return (
                    <div key={mod.slug} className="border border-stone-200 rounded-xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-stone-200 bg-stone-50 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <Puzzle className="w-4 h-4 text-stone-500 shrink-0" />
                                    <span className="font-medium text-stone-800 truncate">{mod.slug}</span>
                                    <span className="text-xs text-stone-500 shrink-0">v{mod.version}</span>
                                </div>
                                {mod.health && mod.health !== 'healthy' && (
                                    <p className="text-xs text-amber-700 mt-1">
                                        Status: {mod.health}
                                        {mod.health_detail?.reason ? ` — ${mod.health_detail.reason}` : ''}
                                    </p>
                                )}
                            </div>
                            <Button
                                size="sm"
                                disabled={!isDirty(mod) || savingSlug === mod.slug}
                                onClick={() => save(mod.slug)}
                            >
                                {savingSlug === mod.slug
                                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Saving…</>
                                    : 'Save permissions'}
                            </Button>
                        </div>

                        <div className="divide-y divide-stone-100">
                            {allScopeNames.map(scopeName => {
                                const scope = scopeCatalog[scopeName];
                                const inCeiling = ceiling.includes(scopeName);
                                const checked = pending.includes(scopeName);
                                const justification = mod.declared_elevated?.includes(scopeName)
                                    ? mod.health_detail?.justifications?.[scopeName]
                                    : null;

                                return (
                                    <label
                                        key={scopeName}
                                        className={`flex items-start gap-3 px-4 py-3 ${
                                            inCeiling ? 'cursor-pointer hover:bg-stone-50' : 'cursor-not-allowed opacity-55'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-1 shrink-0"
                                            checked={checked}
                                            disabled={!inCeiling}
                                            onChange={() => toggleScope(mod.slug, scopeName)}
                                        />
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <code className="text-sm text-stone-800">{scopeName}</code>
                                                {scope?.elevated && (
                                                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                                        <ShieldAlert className="w-3 h-3" />Elevated
                                                    </span>
                                                )}
                                                {!inCeiling && (
                                                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 border border-stone-200">
                                                        <Lock className="w-3 h-3" />Not approved
                                                    </span>
                                                )}
                                            </div>
                                            {scope?.description && (
                                                <p className="text-xs text-stone-500 mt-0.5">{scope.description}</p>
                                            )}
                                            {scope?.elevated && (
                                                <p className="text-xs text-amber-700 mt-1">
                                                    Reads data the acting user cannot see. Every use is recorded in the
                                                    module audit log with a stated reason.
                                                </p>
                                            )}
                                            {justification && (
                                                <p className="text-xs text-stone-600 mt-1 italic border-l-2 border-amber-200 pl-2">
                                                    “{justification}”
                                                </p>
                                            )}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
